import { Controller } from '../utils/controller';

export default class extends Controller {
  static targets = ['sidebar', 'innerList', 'levelOne', 'levelTwo', 'levelThree'];

  declare sidebarTarget: HTMLElement;
  declare innerListTarget: HTMLElement;
  declare innerListTargets: HTMLElement[];
  declare levelOneTargets: HTMLElement[];
  declare levelTwoTargets: HTMLElement[];
  declare levelThreeTargets: HTMLElement[];
  declare hasSidebarTarget: boolean;
  declare hasInnerListTarget: boolean;
  declare hasLevelOneTarget: boolean;
  declare hasLevelTwoTarget: boolean;
  declare hasLevelThreeTarget: boolean;

  LEVEL_1 = 'levelOne';
  LEVEL_2 = 'levelTwo';
  LEVEL_3 = 'levelThree';
  CLOSE_CLASS = 'hide-sidebar';
  OPEN_CLASS = 'translate-x-full';
  UTILITY_NAV_ID = 'sidebar-utility-menu';
  MENU_BUTTON_ID = 'menu-button';
  TAB_KEY = 'Tab';
  ENTER_KEY = 'Enter';

  toggleSidebar(e: Event): void {
    if (this.hasSidebarTarget) {
      this.toggleIbm(e);
      document.body.classList.toggle('scroll-overflow-y-hidden');
      this.sidebarTarget.classList.toggle(this.CLOSE_CLASS);
      this.innerListTargets.forEach((el) => {
        el.classList.add(this.OPEN_CLASS);
      });
    }
  }

  toggleSibling(e: Event): void {
    (e.currentTarget as HTMLElement)?.nextElementSibling?.classList.toggle(this.OPEN_CLASS);
  }

  closeParent(e: Event): void {
    (e.currentTarget as HTMLElement)
      ?.closest('[data-mobile-header-target]')
      ?.classList.toggle(this.OPEN_CLASS);
  }

  toggleIbm(e: Event): void {
    e.stopPropagation();
    const sidebarClosed = this.sidebarTarget.classList.contains(this.CLOSE_CLASS);
    const ibmHidden = document.body.classList.contains('hide-ibm');
    document.body.classList.toggle('hide-ibm', sidebarClosed || !ibmHidden);
  }
  /**
   * Make a category accessible.
   *
   * Accessible means that HTML elements can receive focus if a user presses TAB.
   * Making a category accessible assumes that a category contains subcategories
   * that contain HTML elements and that these elements are made focusable.
   *
   * This function goes through the process of selecting category elements and making
   * them focusable, or not. More specifically, this function makes a category
   * accessible and the parent level categories elements inaccessible.
   *
   * MOTE:
   * This function is not concerned with manipulating the UI. It is only concerned
   * with making UI elements focusable, or not.
   * @param e
   */
  enableCategoryAccessibility(e: KeyboardEvent): void {
    // Make category items accessible
    const selectedCategoryName = this.categoryName(e);
    this.enableCategoryItems(selectedCategoryName);
    this.enableCategoryControlButtons(selectedCategoryName);
    // Make parent category items inaccessible
    const parentCategoryName = this.parentCategoryName(e);
    this.disableCategoryItems(parentCategoryName);
    this.disableCategoryControlButtons(parentCategoryName);
    const parentCategoryLevel = this.level(e);
    this.updateLevelAccessibility({ level: parentCategoryLevel, isEnabled: false });
    if (parentCategoryLevel === this.LEVEL_1) {
      this.disableUtilityNavLinks();
    }
    // Focus on the first element of the selected category.
    // This element is the back button in the sub menu
    const selectedCategoryBackButton = (
      e.currentTarget as HTMLElement
    ).parentElement?.querySelector('[data-sidebar-nav-category-control-buttons] button');
    (selectedCategoryBackButton as HTMLElement)?.focus();
  }

  /**
   * Make a category inaccessible.
   *
   * This is the same as enableCategoryAccessibility(), but in reverse. The parent level
   * category's HTML elements are made accessible, while the current category elements
   * are made inaccessible.
   * @param e
   */
  disableCategoryAccessibility(e: KeyboardEvent): void {
    this.reFocusParentNavItem(e);
    // Make this level's items inaccessible
    const thisLevel = this.level(e);
    this.updateLevelAccessibility({ level: thisLevel, isEnabled: false });
    // Make parent level items accessible
    const parentCategoryName = this.parentCategoryName(e);
    switch (thisLevel) {
      case this.LEVEL_2: {
        // User is returning to level 1. Enable level 1
        this.updateLevelAccessibility({ level: this.LEVEL_1, isEnabled: true });
        this.enableUtilityNavLinks();
        this.enableCategoryControlButtons(parentCategoryName);
        this.disableCategoryControlButtons(this.categoryName(e));
        break;
      }

      case this.LEVEL_3: {
        // User is returning to level 2. Enable level 2
        this.enableCategoryItems(parentCategoryName);
        this.enableCategoryControlButtons(parentCategoryName);
        this.disableCategoryControlButtons(this.categoryName(e));
        break;
      }

      default:
        break;
    }
  }

  /**
   * Makes control buttons accessible
   *
   * Control buttons are the back and the exit buttons visible at the top of
   * each sub menu. These functions enable and disable their accessibility
   * when a user navigates into or out of a sub menu.
   * @param categoryName
   */
  enableCategoryControlButtons(categoryName: string | undefined): void {
    if (!categoryName) {
      return;
    }
    const controlButtons: NodeListOf<HTMLElement> = document.querySelectorAll(
      `[data-sidebar-nav-category-control-buttons="${categoryName}"] button`
    );
    controlButtons.forEach((button) => this.enableAccessibilityForElement(button));
  }

  /**
   * Makes control buttons inaccessible
   * @param categoryName
   */
  disableCategoryControlButtons(categoryName: string | undefined): void {
    if (!categoryName) {
      return;
    }
    const controlButtons: NodeListOf<HTMLElement> = document.querySelectorAll(
      `[data-sidebar-nav-category-control-buttons="${categoryName}"] button`
    );
    controlButtons.forEach((button) => this.disableAccessibilityForElement(button));
  }

  /**
   * Makes Utility nav links accessible
   * Utility Nav links are only displayed when the user is viewing level 1 items.
   * As the user moves into and out of level 1, these items need need to be enabled
   * and disabled.
   */
  enableUtilityNavLinks(): void {
    const utilityNav = document.getElementById(this.UTILITY_NAV_ID);
    const utilityNavLinks = utilityNav?.querySelectorAll('div a');
    utilityNavLinks?.forEach((link: HTMLElement) => this.enableAccessibilityForElement(link));
  }

  /**
   * Makes Utility nav links inaccessible
   */
  disableUtilityNavLinks(): void {
    const utilityNav = document.getElementById(this.UTILITY_NAV_ID);
    const utilityNavLinks = utilityNav?.querySelectorAll('div a');
    utilityNavLinks?.forEach((link: HTMLElement) => this.disableAccessibilityForElement(link));
  }

  /**
   * Update the accessibility for an entire level
   *
   * The gnav can be considered a set of links that are grouped by category.
   * Each category may contain subcategories. Furthermore, each
   * of these subcategories may contain subcategories. Conceptually,
   * website navigation can consist of many category/subcategory pairings like these.
   * The result is a tree-like structure of categories where there is a primary
   * level, secondary level, tertiary level, and so on.
   *
   * This function identifies each level and enables/disables its accessibility.
   */
  updateLevelAccessibility({ level, isEnabled }): void {
    const levelTargets = this.getTargetsForLevel(level);
    levelTargets?.forEach((target) => {
      const levelLinks: NodeListOf<HTMLElement> = target.querySelectorAll(
        `li[data-level="${level}"] > a`
      );
      const levelButtons: NodeListOf<HTMLElement> = target.querySelectorAll(
        `li[data-level="${level}"] > button`
      );
      levelLinks.forEach((levelLink) => {
        isEnabled
          ? this.enableAccessibilityForElement(levelLink)
          : this.disableAccessibilityForElement(levelLink);
      });
      levelButtons.forEach((levelButton) => {
        isEnabled
          ? this.enableAccessibilityForElement(levelButton)
          : this.disableAccessibilityForElement(levelButton);
      });
    });
  }

  /**
   * Return the desired targets for the given level. At this time,
   * the gnav only accounts for 3 levels. If an additional level
   * is needed, it must be added as a stimulus target and here.
   * @param {string} level
   * @returns HTMLElement[]
   */
  getTargetsForLevel(level: string): HTMLElement[] {
    const levels = {
      levelOne: this.levelOneTargets,
      levelTwo: this.levelTwoTargets,
      levelThree: this.levelThreeTargets
    };
    return levels[level];
  }

  enableCategoryItems(categoryName: string | undefined): void {
    if (!categoryName) {
      return;
    }
    this.categoryItems(categoryName).forEach((button) =>
      this.enableAccessibilityForElement(button)
    );
    this.categoryLinkItems(categoryName).forEach((link) =>
      this.enableAccessibilityForElement(link)
    );
  }

  disableCategoryItems(categoryName: string): void {
    this.categoryItems(categoryName).forEach((button) =>
      this.disableAccessibilityForElement(button)
    );
    this.categoryLinkItems(categoryName).forEach((link) =>
      this.disableAccessibilityForElement(link)
    );
  }

  // Wait 500ms for open animation to complete, then focus
  focusSidebarOnOpen(): void {
    if (this.hasSidebarTarget) {
      setTimeout(() => {
        (this.sidebarTarget?.querySelector('nav ul li button') as HTMLElement)?.focus();
        this.updateLevelAccessibility({ level: this.LEVEL_1, isEnabled: true });
        this.enableUtilityNavLinks();
      }, 500);
    }
  }

  // When user tabs out of sidebar, refocus user onto the close button
  focusOutSidebar(e): void {
    const menuButton = document.getElementById(this.MENU_BUTTON_ID);
    const currentTarget = e.currentTarget;
    const firstControlButton = e.target
      .closest('.sidebar-nav-container')
      .querySelector('.mobile-header-menu-controls-container button');

    setTimeout(() => {
      if (menuButton === document.activeElement) {
        return;
      }
      if (this.hasSidebarTarget && this.sidebarTarget?.classList.contains(this.CLOSE_CLASS)) {
        return;
      }
      if (!currentTarget.contains(document.activeElement)) {
        firstControlButton.focus();
      }
    }, 100);
  }

  /**
   * This function acts on keydown events when the back button is in focus. In this context,
   * the back button is button at the top, left of the sidebar nav, across from the X button.
   * Assuming the user presses SHIFT + TAB or ENTER, this function ensures the UI behaves
   * the same as if the user had clicked the back button with their mouse.
   * @param e
   */
  keydown(e): void {
    switch (e.key) {
      case this.TAB_KEY:
        if (e.shiftKey) {
          const parentNavItem = this.parentNavItem(e);
          this.closeParent(e);
          this.disableCategoryAccessibility(e);
          setTimeout(() => {
            parentNavItem?.focus();
          }, 100);
        }
        break;

      case this.ENTER_KEY:
        this.disableCategoryAccessibility(e);
        break;
      default:
        break;
    }
  }
  /**
   * This function's sole purpose is to close the sidebar and return focus back to the
   * hamburger menu. This is for accessibility.
   * @param e
   */
  escape(e): void {
    this.toggleSidebar(e);
    this.focusMenuButton();
    this.disableUtilityNavLinks();
    // Since Esc can be pressed from anywhere, e.target is used to determine the category
    // name and level.
    const categoryName = (
      (e.target as HTMLElement)?.closest('[data-sidebar-nav-category]') as HTMLElement
    )?.dataset.sidebarNavCategory;
    const thisLevel = ((e.target as HTMLElement)?.closest('[data-level]') as HTMLElement)?.dataset
      .level;
    this.updateLevelAccessibility({ level: thisLevel, isEnabled: false });
    this.disableCategoryControlButtons(categoryName);
  }

  reFocusParentNavItem(e): void {
    this.parentNavItem(e)?.focus();
  }

  // Focus on menu button after the sidebar closes
  // Disable accessibility of the level just visited and its control buttons
  returnFocus(e: KeyboardEvent): void {
    this.focusMenuButton();
    this.updateLevelAccessibility({ level: this.level(e), isEnabled: false });
    this.disableUtilityNavLinks();
    this.disableCategoryControlButtons(this.categoryName(e));
  }

  // Put focus on the menu button
  focusMenuButton(): void {
    const menuButton = document.getElementById(this.MENU_BUTTON_ID);
    menuButton?.focus();
  }

  // Restores normal accessibility behavior of focusable elements
  // Focusable elements do not need a tabindex attribute for
  // normal accessibility behavior. If a tabindex attribute exists,
  // remove it.
  enableAccessibilityForElement(element: HTMLElement): void {
    element.removeAttribute('tabindex');
  }

  // Makes a focusable element inaccessible by adding a tabindex
  // value of -1
  disableAccessibilityForElement(element: HTMLElement): void {
    element.tabIndex = -1;
  }

  categoryName(e): string | undefined {
    return (e.currentTarget as HTMLElement)?.closest('[data-sidebar-nav-category]')?.dataset
      .sidebarNavCategory;
  }

  parentCategoryName(e): string {
    return (e.currentTarget as HTMLElement)?.closest('[data-sidebar-nav-parent-category]')?.dataset
      .sidebarNavParentCategory;
  }

  parentNavItem(e): HTMLButtonElement | null | undefined {
    return (
      e.currentTarget as HTMLElement
    )?.parentElement?.parentElement?.parentElement?.querySelector('button');
  }

  level(e): string | undefined {
    return ((e.currentTarget as HTMLElement)?.closest('[data-level]') as HTMLElement)?.dataset
      .level;
  }

  // Button elements, which contain a submenu
  categoryItems(categoryName: string): NodeListOf<HTMLElement> {
    return document.querySelectorAll(
      `li[data-sidebar-nav-parent-category="${categoryName}"] > button`
    );
  }

  categoryLinkItems(categoryName: string): NodeListOf<HTMLElement> {
    return document.querySelectorAll(`li[data-sidebar-nav-parent-category="${categoryName}"] > a`);
  }
}
